import AppCard from '@nxsys/components/AppCard';
import AppIconCard from '@nxsys/components/AppIconCard';
import {
  PAYMENT_MODEL_ENUM,
  getIrelandPaymentModels,
} from '@nxsys/constants/AppConstants';
import { useGetStatisticsPayrollImport } from '@nxsys/hooks/api/dashboard';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Icons } from '@nxsys/types/icons';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import TimesheetModal, {
  TimesheetModalTypeEnum,
} from './components/TimesheetModal';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';

const PayrollImport = () => {
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  // Ireland's models depend on the product too: bureau has PAYE + RCT, umbrella
  // adds UMBRELLA. Hardcoding the bureau pair here filtered every Irish
  // umbrella payroll out of this surface's counts and lists.
  const validPaymentModels = isIreland
    ? getIrelandPaymentModels(isBureau)
    : isBureau
      ? [PAYMENT_MODEL_ENUM.PAYEE]
      : undefined;
  const { value, loading, run } = useGetStatisticsPayrollImport();
  const data = useMemo(() => value?.data, [value]);
  const navigate = useNavigateWithQuery();

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [modalType, setModalType] = useState<TimesheetModalTypeEnum | null>(
    null,
  );

  useEffect(() => {
    run({ paymentModels: validPaymentModels });
  }, []);

  useEffect(() => {
    const handleReload = () => run({ paymentModels: validPaymentModels });
    window.addEventListener('dashboard:reload-payrollImport', handleReload);
    return () => window.removeEventListener('dashboard:reload-payrollImport', handleReload);
  }, []);

  const cbAfterAction = () => {
    run({ paymentModels: validPaymentModels });
    window.dispatchEvent(new CustomEvent('dashboard:reload-clientApprovalReject'));
  }

  const handleOpenModal = (type: TimesheetModalTypeEnum) => {
    setModalType(type);
    setIsOpenModal(true);
  };

  const items = [
    {
      title: '',
      description: 'Payroll Imports',
      icon: <Icons.IconFileImport />,
      onClick: () => navigate('/import/timesheets'),
    },
    {
      title: data?.totalTimesheetPendingApproval,
      description: 'Pending Employer Approval',
      icon: <Icons.IconHomeDollar />,
      onClick: () => handleOpenModal(TimesheetModalTypeEnum.PENDING_APPROVAL),
    },
    {
      title: data?.totalRunPayroll,
      description: 'Run Payroll',
      icon: <Icons.IconMoneyBillMinus />,
      onClick: () =>
        handleOpenModal(TimesheetModalTypeEnum.GENERATE_DRAFT_PAYROLL),
    },
  ];

  return (
    <div className="PayrollImport">
      <AppCard className="p-[24px]" loading={loading}>
        <div className="flex justify-between items-center mb-[24px]">
          <AppIconCard iconName="IconNoteListCheck" title="Payroll Imports" />
        </div>
        <div className="grid grid-cols-3 gap-[16px] max-sm:grid-cols-1">
          {items.map((item) => (
            <div
              key={item.title}
              className="border border-[#CDDDD9] dashboard-hover-card rounded-[8px] p-[16px] flex flex-col justify-between"
              onClick={item.onClick}
            >
              <div className="bg-warning-100 rounded-[8px] p-[8px] w-fit mb-[34px]">
                {item.icon}
              </div>
              <div>
                <div className="text-[22px] font-bold mb-[8px]">
                  {item.title}
                </div>
                <div className="text-[14px] font-bold">{item.description}</div>
              </div>
            </div>
          ))}
        </div>
      </AppCard>
      {modalType && (
        <TimesheetModal
          isOpen={isOpenModal}
          toggle={() => setIsOpenModal(false)}
          type={modalType}
          cbAfterAction={cbAfterAction}
        />
      )}
    </div>
  );
};

export default PayrollImport;
